import { ReactNode, useEffect, useLayoutEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from './store/auth/useAuthStore';
import { publicRoutes } from 'constant/auth-public-routes';

/**
 * useAuthState Hook
 *
 * A custom hook that provides authentication state with mounting status tracking.
 * It synchronizes with the global auth store and ensures components can detect
 * when auth state is fully loaded.
 *
 * @returns {AuthState} An object containing authentication status information
 *   - isMounted: Whether the auth state has been initialized
 *   - isAuthenticated: Whether the user is authenticated
 *   - isMfaEnabled: Whether multi-factor authentication is enabled
 *
 * @example
 * const { isMounted, isAuthenticated, isMfaEnabled } = useAuthState();
 *
 * if (!isMounted) {
 *   return <LoadingSpinner />;
 * }
 *
 * return isAuthenticated ? <AuthenticatedApp /> : <UnauthenticatedApp />;
 */

export const useAuthState = () => {
  const { isAuthenticated, isMfaEnabled } = useAuthStore();
  // Only delay isMounted so the first paint does not flash protected content.
  // Keep isAuthenticated/isMfaEnabled live from the store — copying them through
  // useEffect lagged logout by a frame and left PostAuthMiddleware mounted,
  // which re-fired /iam/me and showed LoadingOverlay.
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  return { isMounted, isAuthenticated, isMfaEnabled };
};

/**
 * ClientMiddleware Component
 *
 * A protection layer that controls rendering based on authentication state,
 * automatically redirecting unauthenticated users away from protected routes.
 *
 * Features:
 * - Blocks access to protected routes for unauthenticated users
 * - Redirects to login page when authentication is required
 * - Allows access to public routes without authentication
 * - Prevents content flash by not rendering until auth state is confirmed
 *
 * @param {Object} props - Component props
 * @param {ReactNode} props.children - Child components to render when access is granted
 * @returns {JSX.Element|null} The children when access is granted, null otherwise
 *
 * @example
 * <ClientMiddleware>
 *   <ProtectedRoutes />
 * </ClientMiddleware>
 */

interface ClientMiddlewareProps {
  children: ReactNode;
}

export const ClientMiddleware: React.FC<ClientMiddlewareProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const currentFullPath = location.pathname + location.search; // Include query parameters
  const { isMounted, isAuthenticated, isMfaEnabled } = useAuthState();
  const isPublicRoute = publicRoutes.includes(currentPath) || currentPath.startsWith('/docs');

  useLayoutEffect(() => {
    if (isMounted && isAuthenticated && currentPath === '/login') {
      const gotoParam = new URLSearchParams(location.search).get('goto');
      navigate(gotoParam || '/chat', { replace: true });
      return;
    }

    if (isMounted && !isAuthenticated && !isPublicRoute && !isMfaEnabled) {
      // Redirect to login with the current full path (including query params) as a URL parameter
      if (currentPath !== '/login') {
        navigate(`/login?goto=${encodeURIComponent(currentFullPath)}`);
      } else {
        navigate('/login');
      }
    }
  }, [
    currentPath,
    currentFullPath,
    isAuthenticated,
    isMounted,
    isMfaEnabled,
    isPublicRoute,
    location.search,
    navigate,
  ]);

  if (isMounted && isAuthenticated && currentPath === '/login') {
    return null;
  }

  if ((!isMounted || !isAuthenticated) && !isPublicRoute) return null;

  return <>{children}</>;
};
